import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Section";
import { RotateCw, Download } from "lucide-react";

const COUNT = 48;
const FRAMES = Array.from({ length: COUNT }, (_, i) => `/band360/frame-${String(i + 1).padStart(2, "0")}.jpg`);

const CALLOUTS = [
  { tag: "Optical Wrist Sensor", title: "Live signal collection", body: "An optical sensor array reads blood-flow signals at the wrist, the same principle used across modern wearables, to derive heart rate and movement context in real time.", color: "#3ad6a0" },
  { tag: "Heart Rate & Activity", title: "Reading your effort", body: "Continuous pulse and motion data reveal whether you are resting or exerting, so protection timing can adapt to how hard your body is actually working.", color: "#ff2d78" },
  { tag: "Skin Temperature", title: "Thermal trend", body: "A skin-contact thermal reading tracks how warm your skin is running, one of the inputs used to estimate perspiration and how quickly protection may wear.", color: "#ffb37e" },
  { tag: "Tactile Prompt", title: "A private nudge", body: "A quiet haptic motor delivers a gentle vibration against the wrist, prompting you the moment action may be needed, without a sound or a screen to check.", color: "#ff2d78" },
  { tag: "Phone Connection", title: "Context, synchronized", body: "A low-energy wireless link keeps the band in sync with your phone, which supplies location, UV index, weather and time outdoors.", color: "#00e5ff" },
  { tag: "On-Wrist Delivery", title: "The solution, already there", body: "A refillable reservoir holds your chosen sunscreen right on the wrist, so the recommended action can happen instantly, no bottle required.", color: "#b794f6" },
];
const PER = COUNT / CALLOUTS.length;

export function Product360() {
  const [frame, setFrame] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [autoSpin, setAutoSpin] = useState(true);
  const containerRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startFrame: 0 });

  // preload frames
  useEffect(() => {
    let done = 0;
    const imgs = FRAMES.map((src) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        done += 1;
        setLoaded(done);
      };
      img.src = src;
      return img;
    });
    return () => imgs.forEach((i) => (i.onload = i.onerror = null));
  }, []);

  const ready = loaded >= COUNT;
  const stage = Math.min(CALLOUTS.length - 1, Math.floor(frame / PER));
  const cur = CALLOUTS[stage];

  // idle auto-spin until first interaction
  useEffect(() => {
    if (!autoSpin || !ready) return;
    const id = setInterval(() => setFrame((f) => (f + 1) % COUNT), 90);
    return () => clearInterval(id);
  }, [autoSpin, ready]);

  const stopAuto = useCallback(() => setAutoSpin(false), []);

  const onPointerDown = (e) => {
    stopAuto();
    drag.current = { active: true, startX: e.clientX, startFrame: frame };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const w = containerRef.current?.offsetWidth || 400;
    const pxPerFrame = w / COUNT;
    const delta = Math.round((e.clientX - drag.current.startX) / pxPerFrame);
    const next = (((drag.current.startFrame - delta) % COUNT) + COUNT) % COUNT;
    setFrame(next);
  };
  const onPointerUp = (e) => {
    drag.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <section className="border-t border-zinc-800 bg-black" data-testid="product-360-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">The Band · 360°</span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.05] text-white max-w-3xl">
            Explore the band. <span className="text-accent">Spin it yourself.</span>
          </h2>
        </Reveal>

        <Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT · band + controls */}
            <div className="lg:col-span-8 flex flex-col items-center">
              <div
                ref={containerRef}
                className="relative w-full aspect-[16/9] select-none touch-none cursor-grab active:cursor-grabbing"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                data-testid="band-360-viewer"
              >
                {FRAMES.map((src, i) => (
                  <img loading="lazy"
                    key={src}
                    src={src}
                    alt="WearSpray band, 360 degree view"
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                    style={{ opacity: i === frame ? 1 : 0 }}
                    data-testid={i === frame ? "band-360-image" : undefined}
                  />
                ))}

                {/* current capability chip */}
                {ready && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full border bg-black/60 backdrop-blur px-3.5 py-2 pointer-events-none" style={{ borderColor: `${cur.color}55` }} data-testid="band-360-chip">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: cur.color }} />
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase" style={{ color: cur.color }}>{cur.tag}</span>
                  </div>
                )}

                {/* loading */}
                {!ready && (
                  <div className="absolute inset-0 flex items-center justify-center" data-testid="band-360-loading">
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
                      Loading {Math.round((loaded / COUNT) * 100)}%
                    </span>
                  </div>
                )}

                {/* drag hint */}
                {ready && autoSpin && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full border border-zinc-700 bg-black/60 backdrop-blur px-4 py-2 pointer-events-none">
                    <RotateCw size={13} className="text-accent animate-spin" />
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-300">Drag to rotate</span>
                  </div>
                )}
              </div>

              {/* slider */}
              <input
                type="range"
                min={0}
                max={COUNT - 1}
                value={frame}
                onChange={(e) => {
                  stopAuto();
                  setFrame(Number(e.target.value));
                }}
                className="mt-8 w-full accent-[#00e5ff] cursor-pointer"
                aria-label="Rotate the band"
                data-testid="band-360-slider"
              />

              {/* stage progress */}
              <div className="w-full flex items-center gap-1.5 mt-4">
                {CALLOUTS.map((c, i) => (
                  <button
                    key={c.tag}
                    onClick={() => { stopAuto(); setFrame(Math.round(i * PER)); }}
                    className="flex-1 h-1 rounded-full transition-all duration-300"
                    style={{ background: i === stage ? c.color : "#3f3f46" }}
                    aria-label={c.tag}
                    data-testid={`band-360-stage-${i}`}
                  />
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 mt-4">
                <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.16em] uppercase text-zinc-500">
                  <RotateCw size={11} className="text-accent" /> Drag the band or slider · stop anytime to read
                </div>
                <a
                  href="/video/wearspray-band-360.mp4"
                  download="WearSpray-Band-360.mp4"
                  data-testid="band-360-download-movie"
                  className="group inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.16em] uppercase text-accent border border-accent/40 rounded-full px-4 py-2 hover:bg-accent/10 transition-colors"
                >
                  <Download size={12} /> Download the movie
                </a>
              </div>
            </div>

            {/* RIGHT · capability definition, revealed only after the visitor interacts */}
            <div className="lg:col-span-4 w-full" data-testid="band-360-definition">
              {autoSpin ? (
                <div
                  className="rounded-2xl border border-dashed border-zinc-800 p-6 md:p-8 text-center lg:text-left"
                  data-testid="band-360-definition-placeholder"
                >
                  <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-600">Explore the parts</span>
                  <p className="font-body text-zinc-500 text-sm md:text-base leading-relaxed mt-3">
                    Grab the band or drag the slider. Whatever part you land on gets explained right here, beside the wearable.
                  </p>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={stage}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="rounded-2xl border p-6 md:p-8"
                    style={{
                      borderColor: `${cur.color}40`,
                      background: "rgba(255,255,255,0.04)",
                      backdropFilter: "blur(18px)",
                      WebkitBackdropFilter: "blur(18px)",
                      boxShadow: `0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)`,
                    }}
                  >
                    <div className="flex items-center gap-2.5 mb-3">
                      <span className="w-2 h-2 rounded-full" style={{ background: cur.color }} />
                      <span className="font-mono text-[11px] tracking-[0.22em] uppercase" style={{ color: cur.color }}>{cur.tag}</span>
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight text-white leading-tight">{cur.title}</h3>
                    <p className="font-body text-zinc-300 text-sm md:text-base leading-relaxed mt-3">{cur.body}</p>
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
